import type { OptionalDTO } from 'src/api';
import type { ColorOption, OptionalCatalogItem } from '../types';
import type { ColorOptionalIndex } from './motornetToForm';

/**
 * Vernici e colori interni a pagamento vivono sia nel select dedicato sia nel
 * picker degli optional: sono la stessa voce di listino. Questi helper tengono
 * allineate le due viste, cosi' il prezzo del colore finisce sempre negli
 * optional salvati e non e' possibile selezionarlo due volte.
 *
 * I colori compresi nel prezzo di listino restano invece governati dal solo
 * select: sono gia' pagati, e in fattura non sono un extra.
 */

/**
 * Colore compreso nel prezzo: di serie oppure senza un prezzo a listino. Non
 * va riportato fra gli optional, dove comparirebbe come un extra da 0 euro.
 */
const isIncludedInListPrice = (item: OptionalCatalogItem): boolean =>
	!!item.isStandard || (item.prezzo ?? 0) <= 0;

/** Optional aggiornati dopo un cambio nel select del colore. */
export const applyColorToOptionals = (
	optionals: OptionalCatalogItem[],
	catalog: OptionalCatalogItem[],
	pool: ColorOption[],
	previousCode: string,
	next: ColorOption | null
): OptionalCatalogItem[] => {
	const previous = pool.find((c) => c.value === previousCode);
	let out = optionals;
	if (previous?.idAccessorio != null) {
		out = out.filter((o) => o.idAccessorio !== previous.idAccessorio);
	}
	if (
		next?.idAccessorio != null &&
		!out.some((o) => o.idAccessorio === next.idAccessorio)
	) {
		const item = catalog.find((i) => i.idAccessorio === next.idAccessorio);
		if (item && !isIncludedInListPrice(item)) out = [...out, item];
	}
	return out;
};

/**
 * Colore da riportare nel select dopo una modifica fatta nel picker.
 * `undefined` = lasciare il select com'e', `null` = svuotarlo.
 */
export const colorFromOptionals = (
	optionals: OptionalCatalogItem[],
	pool: ColorOption[],
	currentCode: string
): ColorOption | null | undefined => {
	const selectedIds = new Set(optionals.map((o) => o.idAccessorio));
	const present = pool.filter(
		(c) => c.idAccessorio != null && selectedIds.has(c.idAccessorio)
	);
	if (present.some((c) => c.value === currentCode)) return undefined;
	if (present.length) return present[0];

	const current = pool.find((c) => c.value === currentCode);
	return current?.idAccessorio != null ? null : undefined;
};

/** I codici di listino si confrontano a meno di spazi e maiuscole. */
export const normalizeOptionCode = (code: string): string =>
	code.trim().toUpperCase();

const lookup = (
	index: Map<string, string>,
	code: string | null | undefined
): string | null => {
	const raw = code?.trim();
	if (!raw) return null;
	return index.get(raw) ?? index.get(normalizeOptionCode(raw)) ?? null;
};

/**
 * ES import manuale: le vernici non arrivano da Autovista, le digita
 * l'operatore fra gli optional. I due select colore pescano da quella lista,
 * cosi' il codice scelto coincide sempre con una voce inserita e il suo prezzo
 * e' gia' conteggiato una volta sola.
 */
export const colorOptionsFromOptionals = (
	optionals: OptionalDTO[] | null | undefined
): ColorOption[] => {
	const seen = new Set<string>();
	const out: ColorOption[] = [];
	for (const optional of optionals ?? []) {
		const value = optional?.codice_costruttore?.trim();
		if (!value) continue;
		const key = normalizeOptionCode(value);
		if (seen.has(key)) continue;
		seen.add(key);
		out.push({
			value,
			label: value,
			description: optional.descrizione?.trim() ?? '',
			price: optional.valore ?? null,
			idDizionario: null,
			// niente catalogo: la voce e' gia' negli optional, non va risincronizzata
			idAccessorio: null,
		});
	}
	return out;
};

/**
 * Stessa regola del form manuale, applicata a una riga di import: un codice
 * colore che e' anche voce di listino a pagamento entra fra gli optional, cosi'
 * il suo prezzo viene conteggiato. I colori di serie e quelli sconosciuti non
 * sono a catalogo e restano fuori.
 */
export const mergeColorOptionalCodes = (
	optionalCodes: string[],
	index: ColorOptionalIndex,
	colors: { exterior?: string | null; interior?: string | null }
): { codes: string[]; fromColor: string[] } => {
	const seen = new Set<string>();
	const codes: string[] = [];
	for (const raw of optionalCodes ?? []) {
		const code = raw?.trim();
		if (!code) continue;
		const key = normalizeOptionCode(code);
		if (seen.has(key)) continue;
		seen.add(key);
		codes.push(code);
	}

	const fromColor: string[] = [];
	const resolved = [
		lookup(index.exterior, colors.exterior),
		lookup(index.interior, colors.interior),
	];
	for (const code of resolved) {
		if (!code) continue;
		const key = normalizeOptionCode(code);
		if (seen.has(key)) continue;
		seen.add(key);
		codes.push(code);
		fromColor.push(code);
	}

	return { codes, fromColor };
};
